import {
  Box,
  Collapse,
  debounce,
  Typography,
  useMediaQuery,
} from '@mui/material';
import FilterActionButtons from './FilterActionButtons';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CandidateSportFilter from './CandidateSportFilter';
import { useThemeContext } from '../../../../../theme/ThemeContextProvider';
import FiltersIcon from '../../../../../components/Icons/FiltersIcon';
import ArrowDownIcon from '../../../../../components/Icons/ArrowDownIcon';
import { useGetPaginatedSportsQuery } from '../../../../../store/apiSlice/sportApi';
import SkeletonList from '../../../../../components/MUIComponents/SkeletonList';
import CandidatesWorkExperienceFilter from './CandidatesWorkExperienceFilter';
import { useGetUniqueJobTitlesQuery } from '../../../../../store/apiSlice/workExperienceApi';
import CandidatesEmploymentTypeFilter from './CandidatesEmploymentTypeFilter';
import CandidatesWorkTypeFilter from './CandidatesWorkTypeFilter';
import {
  CityOption,
  getPaginatedCities,
  getPaginatedCountries,
} from '../../../../../utils/coutry-city-utils';
import CandidateCountryFilter from './CandidateCountryFilter';
import { ICountry } from 'country-state-city';
import {
  ISelectedCity,
  ISelectedCountry,
} from '../../../../../interfaces/country.interface';
import CandidateCityFilter from './CandidateCityFilter';
import { ISport } from '../../../../../interfaces/sport.interface';
import { withUnderScore } from '../../../../../utils/withUnderScore';
import FilterLayout from '../../../../../layouts/FilterLayout';

const CandidatesFilters = () => {
  const { theme } = useThemeContext();
  const mobileView = useMediaQuery(theme.breakpoints.down('lg'));

  const [_searchParams, setSearchParams] = useSearchParams();

  const [isScoreExpanded, setIsScoreExpanded] = useState(false);
  const [sportLimit, setSportLimit] = useState<number>(5);
  const [countryLimit, setCountryLimit] = useState<number>(5);
  const [cityLimit, setCityLimit] = useState<number>(5);
  const [jobTitlesLimit, setJobTitlesLimit] = useState<number>(5);
  const [activeAthlete, setActiveAthlete] = useState<boolean>(false);
  const [selectedJobTitles, setSelectedJobTitles] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<
    ISelectedCountry[]
  >([]);
  const [selectedSports, setSelectedSports] = useState<ISport[]>([]);
  const [selectedCities, setSelectedCities] = useState<ISelectedCity[]>([]);
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState<
    string[]
  >([]);
  const [selectedWorkTypes, setSelectedWorkTypes] = useState<string[]>([]);
  const [searchJobTitle, setSearchJobTitle] = useState<string>('');
  const [searchSportName, setSearchSportName] = useState<string>('');
  const [searchCountryName, setSearchCountryName] = useState<string>('');
  const [searchCityName, setSearchCityName] = useState<string>('');
  const [resetTrigger, setResetTrigger] = useState(false);
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [totalCountries, setTotalCountries] = useState<number>(0);
  const [isCountryLoading, setIsCountryLoading] = useState<boolean>(false);
  const [isCountryError, setIsCountryError] = useState<boolean>(false);

  const [cities, setCities] = useState<CityOption[]>([]);
  const [totalCities, setTotalCities] = useState<number>(0);
  const [isCitiesLoading, setIsCitiesLoading] = useState<boolean>(false);
  const [isCitiesError, setIsCitiesError] = useState<boolean>(false);

  useEffect(() => {
    const getCountries = async () => {
      try {
        setIsCountryLoading(true);
        const countryPaginatedResponse = getPaginatedCountries(
          searchCountryName,
          1,
          countryLimit
        );
        setCountries(countryPaginatedResponse.data);
        setTotalCountries(countryPaginatedResponse.total);
        //eslint-disable-next-line
      } catch (error) {
        setIsCountryError(true);
      } finally {
        setIsCountryLoading(false);
      }
    };
    getCountries();
  }, [searchCountryName, countryLimit]);

  useEffect(() => {
    const getCities = async () => {
      try {
        setIsCitiesLoading(true);
        const countryIsoCodes = selectedCountries.map(
          (country) => country.isoCode
        );
        const citiesPaginatedResponse = getPaginatedCities(
          countryIsoCodes,
          searchCityName,
          1,
          cityLimit
        );
        setCities(citiesPaginatedResponse.data);
        setTotalCities(citiesPaginatedResponse.total);
        // eslint-disable-next-line
      } catch (error) {
        setIsCitiesError(true);
      } finally {
        setIsCitiesLoading(false);
      }
    };
    getCities();
  }, [searchCityName, cityLimit, selectedCountries]);

  const handleFilterReset = () => {
    setSelectedWorkTypes([]);
    setSelectedEmploymentTypes([]);
    setSelectedJobTitles([]);
    setSelectedSports([]);
    setSelectedCountries([]);
    setSelectedCities([]);
    setActiveAthlete(false);
    setSportLimit(5);
    setSearchSportName('');
    setSearchJobTitle('');
    setSearchCountryName('');
    setSearchCityName('');
    setCountryLimit(5);
    setCityLimit(5);
    setResetTrigger((prev) => !prev);

    setSearchParams((prevParams) => {
      prevParams.set('page', '1');
      prevParams.set('sports', '');
      prevParams.set('countries', '');
      prevParams.set('cities', '');
      prevParams.set('jobTitles', '');
      prevParams.set('employmentTypes', '');
      prevParams.set('workTypes', '');
      prevParams.set('athleteActive', '');

      return prevParams;
    });
  };

  const handleFilterApply = () => {
    const formattedSports = selectedSports
      ? selectedSports
          .map((sport) => sport.name)
          .map(withUnderScore)
          .join(',')
      : '';

    setSearchParams((prevParams) => {
      prevParams.set('page', '1');
      prevParams.set('sports', formattedSports);
      prevParams.set(
        'countries',
        selectedCountries.map((country) => country.name).join(',')
      );
      prevParams.set(
        'cities',
        selectedCities.map((city) => city.name).join(',')
      );
      prevParams.set('jobTitles', selectedJobTitles.join(','));
      prevParams.set('employmentTypes', selectedEmploymentTypes.join(','));
      prevParams.set('workTypes', selectedWorkTypes.join(','));
      prevParams.set('athleteActive', `${activeAthlete}`);

      return prevParams;
    });
  };

  const {
    data: sportsPaginationResponse,
    isLoading: isSportLoading,
    isError: isSportError,
    error: sportError,
  } = useGetPaginatedSportsQuery({
    page: 1,
    limit: sportLimit,
    search: searchSportName,
    sort: 'asc',
  });

  const {
    data: jobTitlesPaginationResponse,
    isLoading: isJobTitlesLoading,
    isError: isJobTitlesError,
    error: jobTitlesError,
  } = useGetUniqueJobTitlesQuery({
    page: 1,
    limit: jobTitlesLimit,
    search: searchJobTitle,
    sort: 'asc',
  });


  const displayJobTitles = useMemo(() => {
    const apiTitles =
      jobTitlesPaginationResponse?.data?.map((item) => item.title) ?? [];
    const unselectedApiTitles = apiTitles.filter(
      (title) => !selectedJobTitles.includes(title)
    );
    return [...selectedJobTitles, ...unselectedApiTitles];
  }, [jobTitlesPaginationResponse?.data, selectedJobTitles]);

  const handleSportShowMore = () => {
    setSportLimit((prevLimit) => prevLimit + 5);
  };
  const handleJobTitlesShowMore = () => {
    setJobTitlesLimit((prevLimit) => prevLimit + 5);
  };

  const handleCountriesShowMore = () => {
    setCountryLimit((prevLimit) => prevLimit + 5);
  };
  const handleCitiesShowMore = () => {
    setCityLimit((prevLimit) => prevLimit + 5);
  };

  const toggleExpand = () => {
    setIsScoreExpanded((prevState) => !prevState);
  };

  const handleJobTitleChange = (jobTitle: string): void => {
    setSelectedJobTitles((prev) =>
      prev.includes(jobTitle)
        ? prev.filter((title) => title !== jobTitle)
        : [...prev, jobTitle]
    );
  };

  const handleSelectedEmploymentTypes = (employmentType: string): void => {
    setSelectedEmploymentTypes((prev) =>
      prev.includes(employmentType)
        ? prev.filter((data) => data !== employmentType)
        : [...prev, employmentType]
    );
  };

  const handleSelectedWorkTypes = (workType: string): void => {
    setSelectedWorkTypes((prev) =>
      prev.includes(workType)
        ? prev.filter((data) => data !== workType)
        : [...prev, workType]
    );
  };

  const handleSelectedCountry = (country: ISelectedCountry): void => {
    setSelectedCountries((prev) =>
      prev.some((selected) => selected.isoCode === country.isoCode)
        ? prev.filter((selected) => selected.isoCode !== country.isoCode)
        : [...prev, country]
    );
  };
  const handleSelectedCity = (city: ISelectedCity): void => {
    setSelectedCities((prev) =>
      prev.some((selected) => selected.id === city.id)
        ? prev.filter((selected) => selected.id !== city.id)
        : [...prev, city]
    );
  };

  const handleSelectedSport = (sport: ISport): void => {
    setSelectedSports((prev) =>
      prev.some((selected) => selected.id === sport.id)
        ? prev.filter((selected) => selected.id !== sport.id)
        : [...prev, sport]
    );
  };

  const handleSearchJobTitleChange = useMemo(
    () => debounce((value: string) => setSearchJobTitle(value), 300),
    []
  );

  const handleSearchSportNameChange = useMemo(
    () => debounce((value: string) => setSearchSportName(value), 300),
    []
  );
  const handleSearchCountryNameChange = useMemo(
    () => debounce((value: string) => setSearchCountryName(value), 300),
    []
  );

  const handleSearchCityNameChange = useMemo(
    () => debounce((value: string) => setSearchCityName(value), 300),
    []
  );

  const candidatesFiltersBlock = (
    <>
      <Box display={'flex'} flexDirection={'column'}>
        {!mobileView && (
          <Typography
            component="h3"
            variant="h3"
            color={'text.primary'}
            textAlign={'left'}
            sx={{
              padding: '1.375rem 1rem',
              gap: '22px',
            }}
            borderBottom={`1px solid ${theme.palette.background.lightDark}`}
          >
            Filters
          </Typography>
        )}
        {isSportLoading ? (
          skeletonFilters
        ) : (
          <CandidateSportFilter
            sports={sportsPaginationResponse?.data ?? []}
            isLoading={isSportLoading}
            isError={isSportError}
            totalSportsCount={sportsPaginationResponse?.total ?? 0}
            selectedSports={selectedSports}
            handleSelectedSport={handleSelectedSport}
            label={'sports'}
            error={sportError}
            onShowMore={handleSportShowMore}
            handleSearchChange={handleSearchSportNameChange}
            resetTrigger={resetTrigger}
          />
        )}{' '}
        {isCountryLoading ? (
          skeletonFilters
        ) : (
          <FilterLayout name={'Location'}>
            <CandidateCountryFilter
              countries={countries ?? []}
              totalCountryCount={totalCountries}
              label={'Location'}
              isLoading={isCountryLoading}
              isError={isCountryError}
              onShowMore={handleCountriesShowMore}
              handleSearchChange={handleSearchCountryNameChange}
              handleCountryChange={handleSelectedCountry}
              selectedCountries={selectedCountries}
              resetTrigger={resetTrigger}
            />
            {isCitiesLoading ? (
              skeletonFilters
            ) : (
              <CandidateCityFilter
                cities={cities ?? []}
                totalCityCount={totalCities}
                label={'City'}
                isLoading={isCitiesLoading}
                isError={isCitiesError}
                onShowMore={handleCitiesShowMore}
                handleSearchChange={handleSearchCityNameChange}
                handleCityChange={handleSelectedCity}
                selectedCities={selectedCities}
                resetTrigger={resetTrigger}
                selectedCountries={selectedCountries}
              />
            )}
          </FilterLayout>
        )}
        {isJobTitlesLoading ? (
          skeletonFilters
        ) : (
          <CandidatesWorkExperienceFilter
            jobTitles={displayJobTitles}
            totalJobTitlesCount={jobTitlesPaginationResponse?.total ?? 0}
            activeAthlete={activeAthlete}
            setActiveAthlete={setActiveAthlete}
            isLoading={isJobTitlesLoading}
            isError={isJobTitlesError}
            label={'Work experience'}
            error={jobTitlesError}
            onShowMore={handleJobTitlesShowMore}
            handleJobTitleChange={handleJobTitleChange}
            selectedJobTitles={selectedJobTitles}
            handleSearchChange={handleSearchJobTitleChange}
            resetTrigger={resetTrigger}
          />
        )}
        <CandidatesEmploymentTypeFilter
          label={'Employment Type'}
          handleSelectedEmploymentTypes={handleSelectedEmploymentTypes}
          selectedEmploymentTypes={selectedEmploymentTypes}
        />
        <CandidatesWorkTypeFilter
          label={'Work Type'}
          handleSelectedWorkTypes={handleSelectedWorkTypes}
          selectedWorkTypes={selectedWorkTypes}
        />
      </Box>

      <FilterActionButtons
        handleFilterReset={handleFilterReset}
        handleFilterApply={handleFilterApply}
      />
    </>
  );

  return (
    <Box
      sx={{
        minWidth: 227,
        width: mobileView ? '100%' : 314,
        borderRadius: '1px',
        bgcolor: 'background.white',
      }}
    >
      {mobileView && (
        <Box
          display={'flex'}
          sx={{
            gap: '22px',
          }}
        >
          <Box
            width={'100%'}
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            sx={{
              padding: '1.375rem 1rem',
            }}
          >
            <Typography
              component="h3"
              variant="h3"
              color={'text.primary'}
              textAlign={'left'}
              display={'flex'}
              alignItems={'center'}
            >
              <FiltersIcon />
              Filters
            </Typography>
            <Box display="flex" gap="12px" onClick={toggleExpand}>
              <ArrowDownIcon
                sx={{
                  transform: isScoreExpanded ? 'rotate(180deg)' : '',
                  width: '1.125rem',
                  height: '1.125rem',
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
      {mobileView ? (
        <Collapse in={isScoreExpanded}>{candidatesFiltersBlock}</Collapse>
      ) : (
        candidatesFiltersBlock
      )}
    </Box>
  );
};

export default CandidatesFilters;

const skeletonFilters = (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '1.375rem',
      padding: '1.375rem 1rem',
    }}
  >
    <SkeletonList
      count={1}
      sx={{
        minHeight: '3rem',
      }}
    />
  </Box>
);